import React, { useEffect, useRef, useState } from "react";
import { Chart } from "chart.js";
import { TreemapController, TreemapElement } from "chartjs-chart-treemap";
import { doc, getDoc } from "firebase/firestore";
import { db } from "../firebaseConfig";
import "./TreeMapChart.css"; // ✨ Import do CSS customizado

Chart.register(TreemapController, TreemapElement);

const TreeMapChart = ({ chamadas = [] }) => {
  const chartRef = useRef(null);
  const chartInstance = useRef(null);
  const [dados, setDados] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [cacheUsed, setCacheUsed] = useState(false);

  // 🔹 Função para validar se os dados carregados do cache são válidos
  const dadosSaoValidos = (dados) => {
    return Array.isArray(dados) && dados.length > 0 && dados.every((item) => item.name && item.value);
  };

  // 🔹 Gerar cores gradientes para os setores
  const gerarCoresGradiente = (index, total) => {
    const cores = [
      { bg: "#6366f1", border: "#818cf8" },      // Indigo
      { bg: "#ec4899", border: "#f472b6" },      // Rosa
      { bg: "#10b981", border: "#34d399" },      // Verde
      { bg: "#f59e0b", border: "#fbbf24" },      // Laranja
      { bg: "#8b5cf6", border: "#a78bfa" },      // Roxo
      { bg: "#06b6d4", border: "#22d3ee" },      // Ciano
      { bg: "#ef4444", border: "#f87171" },      // Vermelho
      { bg: "#14b8a6", border: "#2dd4bf" },      // Teal
    ];
    return cores[index % cores.length];
  };

  // 🥥 Buscar dados resumidos do Firestore
  const fetchResumoChamadas = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const docRef = doc(db, "chamadasResumo", "setores");
      const docSnap = await getDoc(docRef);

      if (docSnap.exists()) {
        console.log("📊 Dados carregados do Firestore:", docSnap.data());
        const dadosFormatados = Object.entries(docSnap.data()).map(([setor, quantidade]) => ({
          name: setor,
          value: quantidade,
        }));

        if (dadosSaoValidos(dadosFormatados)) {
          setDados(dadosFormatados);
          salvarCache(dadosFormatados);
          setCacheUsed(false);
          setIsLoading(false);
        } else {
          console.warn("⚠️ Dados inválidos encontrados no Firestore.");
          setError("Dados inválidos recebidos do servidor");
          setIsLoading(false);
        }
      } else {
        console.warn("⚠️ Nenhum dado de resumo encontrado no Firestore.");
        setError("Nenhum dado disponível no servidor");
        setIsLoading(false);
      }
    } catch (error) {
      console.error("❌ Erro ao buscar resumo de chamadas:", error);
      setError("Erro ao carregar dados do servidor");
      setIsLoading(false);
    }
  };

  // 🔹 Salvar dados em cache
  const salvarCache = (dados) => {
    try {
      localStorage.setItem("chamadasCache", JSON.stringify(dados));
      localStorage.setItem("chamadasCacheTimestamp", new Date().toISOString());
    } catch (error) {
      console.warn("⚠️ Erro ao salvar cache:", error);
    }
  };

  // 🔹 Carregar dados do cache
  const carregarCache = () => {
    try {
      const cache = localStorage.getItem("chamadasCache");
      return cache ? JSON.parse(cache) : null;
    } catch (error) {
      console.warn("⚠️ Erro ao carregar cache:", error);
      return null;
    }
  };

  // 🔹 Efeito para carregar dados iniciais
  useEffect(() => {
    const cache = carregarCache();
    if (dadosSaoValidos(cache)) {
      console.log("📊 Dados carregados do cache!");
      setDados(cache);
      setCacheUsed(true);
      setIsLoading(false);
      // Atualizar dados do Firestore em background
      fetchResumoChamadas();
    } else {
      console.warn("⚠️ Cache inválido. Buscando do Firestore...");
      fetchResumoChamadas();
    }
  }, []);

  // 🔹 Efeito para renderizar o gráfico
  useEffect(() => {
    if (!dadosSaoValidos(dados)) {
      console.warn("⚠️ Nenhum dado disponível para o TreeMap.");
      return;
    }

    if (chartInstance.current) {
      chartInstance.current.destroy();
    }

    const ctx = chartRef.current?.getContext("2d");
    if (!ctx) return;

    try {
      chartInstance.current = new Chart(ctx, {
        type: "treemap",
        data: {
          datasets: [
            {
              tree: dados,
              key: "value",
              backgroundColor: dados.map((_, idx) => gerarCoresGradiente(idx, dados.length).bg),
              borderColor: dados.map((_, idx) => gerarCoresGradiente(idx, dados.length).border),
              borderWidth: 2.5,
              spacing: 4,
              tile: "squarify",
              labels: {
                display: true,
                font: { size: 13, weight: "bold" },
                color: "#ffffff",
                formatter: (ctx) => {
                  if (ctx.raw) {
                    const total = dados.reduce((sum, item) => sum + item.value, 0);
                    const percentual = ((ctx.raw.value / total) * 100).toFixed(1);
                    return `${ctx.raw.name}\n${ctx.raw.value} chamadas\n${percentual}%`;
                  }
                  return "Sem Info";
                },
              },
            },
          ],
        },
        options: {
          plugins: {
            legend: { display: false },
            tooltip: {
              enabled: true,
              backgroundColor: "rgba(30, 41, 59, 0.95)",
              titleFont: { size: 13, weight: "bold" },
              bodyFont: { size: 12 },
              padding: 12,
              borderColor: "#6366f1",
              borderWidth: 1,
              cornerRadius: 8,
              callbacks: {
                title: (context) => {
                  const item = context[0].raw;
                  return item ? item.name : "Setor";
                },
                label: (tooltipItem) => {
                  const item = tooltipItem.raw;
                  if (item) {
                    const total = dados.reduce((sum, d) => sum + d.value, 0);
                    const percentual = ((item.value / total) * 100).toFixed(1);
                    return [
                      `Chamadas: ${item.value}`,
                      `Percentual: ${percentual}%`,
                      `Total: ${total}`,
                    ];
                  }
                  return "Sem informação";
                },
              },
            },
          },
          maintainAspectRatio: false,
          animation: {
            duration: 1000,
            easing: "easeInOutQuart",
          },
        },
      });
    } catch (error) {
      console.error("❌ Erro ao renderizar o TreeMapChart:", error);
      setError("Erro ao renderizar o gráfico");
    }

    return () => {
      if (chartInstance.current) {
        chartInstance.current.destroy();
      }
    };
  }, [dados]);

  return (
    <div className="treemap-container">
      {/* Loading State */}
      {isLoading && !dadosSaoValidos(dados) && (
        <div className="treemap-loading">
          <div className="spinner"></div>
          <p>Carregando dados do mapa...</p>
        </div>
      )}

      {/* Error State */}
      {error && !dadosSaoValidos(dados) && (
        <div className="treemap-error">
          <span className="error-icon">⚠️</span>
          <p>{error}</p>
          <button onClick={fetchResumoChamadas} className="retry-button">
            Tentar Novamente
          </button>
        </div>
      )}

      {/* Cache Indicator */}
      {cacheUsed && dadosSaoValidos(dados) && (
        <div className="cache-indicator">
          <span className="cache-badge">📦 Dados do cache</span>
        </div>
      )}

      {/* Chart Canvas */}
      {dadosSaoValidos(dados) && (
        <div className="treemap-wrapper">
          <canvas ref={chartRef} className="treemap-canvas" />
        </div>
      )}

      {/* Empty State */}
      {!isLoading && !error && !dadosSaoValidos(dados) && (
        <div className="treemap-empty">
          <span className="empty-icon">📊</span>
          <p>Nenhum dado disponível para exibir</p>
        </div>
      )}

      {/* Stats Footer */}
      {dadosSaoValidos(dados) && (
        <div className="treemap-stats">
          <div className="stat-item">
            <span className="stat-label">Total de Setores</span>
            <span className="stat-value">{dados.length}</span>
          </div>
          <div className="stat-item">
            <span className="stat-label">Total de Chamadas</span>
            <span className="stat-value">{dados.reduce((sum, item) => sum + item.value, 0)}</span>
          </div>
          <div className="stat-item">
            <span className="stat-label">Média por Setor</span>
            <span className="stat-value">
              {(dados.reduce((sum, item) => sum + item.value, 0) / dados.length).toFixed(1)}
            </span>
          </div>
        </div>
      )}
    </div>
  );
};

export default TreeMapChart;
